import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  ChakraProvider,
  extendTheme,
  Box,
  VStack,
  Heading,
  Input,
  Button,
  HStack,
  Text,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Select as ChakraSelect,
  useDisclosure,
  useToast,
  InputGroup,
  InputLeftElement,
  Divider,
  Checkbox,
  Spinner,
  Center,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  SimpleGrid,
  Avatar,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import md5 from 'blueimp-md5';
import Select from 'react-select';

declare global {
  interface Window {
    userData: {
      access: any;
      access_token: string;
      email: string;
      name: string;
      slack_id: string;
      [key: string]: any;
    };
  }
}

const theme = extendTheme({
  fonts: {
    heading: 'Inter, sans-serif',
    body: 'Inter, sans-serif',
  },
  styles: {
    global: {
      body: {
        bg: 'gray.50',
        color: 'gray.800',
        minH: '100vh',
      },
    },
  },
  components: {
    Container: {
      baseStyle: {
        maxW: '100%',
        px: { base: 4, md: 8, lg: 12 },
      },
    },
  },
});

interface User {
  name: string;
  full_name?: string;
  email?: string;
  profile_phone?: string;
  tags?: string;
  channels?: string[];
  channels_with_tags?: any[];
  admin?: boolean;
  template?: string;
  profile?: {
    picture_thumb?: string;
  };
}

interface Channel {
  name: string;
  shared?: boolean;
  invisible?: boolean;
  tags?: string;
  profile?: {
    picture_thumb?: string;
  };
}

const tagOptions = [
  { label: 'Other', value: '' },
  { label: 'Ardsley PD', value: 'ardsley' },
  { label: 'ATF', value: 'atf' },
  { label: 'Bedford PD', value: 'bedford' },
  { label: 'Brewster PD', value: 'brewster' },
  { label: 'Briarcliff Manor PD', value: 'briarcliff' },
  { label: 'Bronxville PD', value: 'bronxville' },
  { label: 'Buchanan PD', value: 'buchanan' },
  { label: 'Carmel PD', value: 'carmel' },
  { label: 'City of Rye PD', value: 'ryecity' },
  { label: 'Clarkstown PD', value: 'clarkstown' },
  { label: 'Cold Spring PD', value: 'coldspring' },
  { label: 'Croton PD', value: 'croton' },
  { label: 'CT State Police', value: 'ctsp' },
  { label: 'Danbury PD', value: 'danbury' },
  { label: 'DEA', value: 'dea' },
  { label: 'DEP', value: 'dep' },
  { label: 'Dobbs Ferry PD', value: 'dobbsferry' },
  { label: 'Eastchester PD', value: 'eastchester' },
  { label: 'Elmsford PD', value: 'elmsford' },
  { label: 'Fairfield PD', value: 'fairfield' },
  { label: 'FBI', value: 'fbi' },
  { label: 'FDNY', value: 'fdny' },
  { label: 'Greenburgh PD', value: 'greenburgh' },
  { label: 'Greenwich PD', value: 'greenwich' },
  { label: 'Harrison PD', value: 'harrison' },
  { label: 'Hastings PD', value: 'hastings' },
  { label: 'Irvington PD', value: 'irvington' },
  { label: 'Kent PD', value: 'kent' },
  { label: 'Larchmont PD', value: 'larchmont' },
  { label: 'Lewisboro PD', value: 'lewisboro' },
  { label: 'Mt Pleasant PD', value: 'mppd' },
  { label: 'Mt Vernon PD', value: 'mvpd' },
  { label: 'MTA PD', value: 'mtapd' },
  { label: 'New Castle PD', value: 'newcastle' },
  { label: 'New Rochelle PD', value: 'nrpd' },
  { label: 'North Castle PD', value: 'northcastle' },
  { label: 'North Salem PD', value: 'northsalem' },
  { label: 'NY State Police', value: 'nysp' },
  { label: 'NYPD', value: 'nypd' },
  { label: 'NYS Attorney General', value: 'nysag' },
  { label: 'Ossining PD', value: 'ossining' },
  { label: 'Palisades Parkway Police', value: 'pipd' },
  { label: 'Peekskill PD', value: 'peekskill' },
  { label: 'Pelham Manor PD', value: 'pelhammanor' },
  { label: 'Pelham PD', value: 'pelham' },
  { label: 'Pennsylvania State Police', value: 'pasp' },
  { label: 'Pleasantville PD', value: 'pleasantville' },
  { label: 'Port Authority PD', value: 'papd' },
  { label: 'Port Chester PD', value: 'portchester' },
  { label: 'Poughkeepsie PD', value: 'poughkeepsie' },
  { label: 'Pound Ridge PD', value: 'poundridge' },
  { label: 'Putnam County Sheriff\'s Office', value: 'pcso' },
  { label: 'Ridgefield PD', value: 'ridgefield' },
  { label: 'Rye Brook PD', value: 'ryebrook' },
  { label: 'Scarsdale PD', value: 'scarsdale' },
  { label: 'Sleepy Hollow PD', value: 'shpd' },
  { label: 'Somers PD', value: 'somers' },
  { label: 'Stamford PD', value: 'stamford' },
  { label: 'SUNY Purchase PD', value: 'suny' },
  { label: 'Tarrytown PD', value: 'tarrytown' },
  { label: 'Town of Mamaroneck PD', value: 'tompd' },
  { label: 'Tuckahoe PD', value: 'tuckahoe' },
  { label: 'US Marshals Service', value: 'usms' },
  { label: 'Village of Mamaroneck PD', value: 'vompd' },
  { label: 'Wappingers PD', value: 'wappingers' },
  { label: 'Westchester County DA', value: 'wcda' },
  { label: 'Westchester County Probation', value: 'wcprobation' },
  { label: 'Westport PD', value: 'westport' },
  { label: 'White Plains PD', value: 'wppd' },
  { label: 'Yonkers PD', value: 'yonkers' },
  { label: 'Yorktown PD', value: 'yorktown' },
];

const AddUserForm: React.FC<{ onSuccess: (username: string) => void }> = ({ onSuccess }) => {
  const [form, setForm] = useState({
    name: '',
    password: '',
    email: '',
    profile_phone: '',
    full_name: '',
    tags: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const toast = useToast();

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      setPasswordError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(password)) {
      setPasswordError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(password)) {
      setPasswordError('Password must contain at least one number');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === 'password') {
      validatePassword(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword(form.password)) {
      toast({
        title: 'Invalid Password',
        description: passwordError,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/zello/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          password: md5(form.password),
          admin: false,
          limited_access: false,
          gateway: false,
          add: true
        }),
      });
      const data = await res.json();
      if (data.code === '200') {
        // If user creation successful, assign to GPS template
        try {
          const formData = new URLSearchParams();
          formData.append('users[]', form.name);
          
          const response = await fetch('/zello/user/assigntemplate/name/GPS', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString(),
          });
          
          const templateData = await response.json();
          if (templateData.code !== '200') {
            console.error('Error assigning to GPS template:', templateData);
          }
        } catch (error) {
          console.error('Error assigning to GPS template:', error);
        }

        toast({
          title: 'User added successfully!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setForm({ name: '', password: '', email: '', profile_phone: '', full_name: '', tags: '' });
        onSuccess(form.name);
      } else if (data.code === '405' && data.status === 'User already exists') {
        toast({
          title: 'Error: User Already Exists',
          description: 'A user with this username already exists in the system.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
          containerStyle: {
            width: '100%',
            maxWidth: '100%',
            margin: '0 auto',
            textAlign: 'center',
          },
        });
      } else {
        toast({
          title: data.message || 'Error adding user',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error adding user',
        description: 'An unexpected error occurred. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel>Username</FormLabel>
          <Input name="name" value={form.name} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired isInvalid={!!passwordError}>
          <FormLabel>Password</FormLabel>
          <Input 
            name="password" 
            type="password"
            value={form.password} 
            onChange={handleChange}
          />
          {passwordError && (
            <Text color="red.500" fontSize="sm" mt={1}>
              {passwordError}
            </Text>
          )}
        </FormControl>
        <FormControl>
          <FormLabel>Email</FormLabel>
          <Input name="email" value={form.email} onChange={handleChange} />
        </FormControl>
        <FormControl>
          <FormLabel>Cell Phone</FormLabel>
          <Input name="profile_phone" value={form.profile_phone} onChange={handleChange} />
        </FormControl>
        <FormControl>
          <FormLabel>Full Name</FormLabel>
          <Input name="full_name" value={form.full_name} onChange={handleChange} />
        </FormControl>
        <FormControl>
          <FormLabel>Agency</FormLabel>
          <ChakraSelect name="tags" value={form.tags} onChange={handleChange}>
            <option value="">Other</option>
            {tagOptions.filter(opt => opt.value !== '').map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </ChakraSelect>
        </FormControl>
        <Button 
          type="submit" 
          colorScheme="blue" 
          isLoading={isSubmitting}
          isDisabled={!!passwordError}
        >
          Add User
        </Button>
      </VStack>
    </form>
  );
};

// In-memory cache for loaded image URLs
const imageCache: { [url: string]: boolean } = {};

// Utility to get initials from a name: first letter of last two words, ignoring parentheses
function getInitials(name?: string): string {
  if (!name) return '';
  // Remove parentheses and their contents
  const cleaned = name.replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const words = cleaned.split(' ').filter(Boolean);
  if (words.length === 1) return words[0][0]?.toUpperCase() || '';
  const lastTwo = words.slice(-2);
  return lastTwo.map(w => w[0]?.toUpperCase() || '').join('');
}

function getProxiedUrl(url?: string) {
  if (!url) return undefined;
  // If already proxied, return as is
  if (url.startsWith('/zello/proxy-image?url=')) return url;
  return `/zello/proxy-image?url=${encodeURIComponent(url)}`;
}

const CachedAvatar: React.FC<{
  src?: string;
  name?: string;
  size?: string;
  bg?: string;
  style?: React.CSSProperties;
}> = ({ src, name, size = 'md', bg = 'gray.200', style }) => {
  const [imgSrc, setImgSrc] = React.useState<string | undefined>(src);
  const [error, setError] = React.useState(false);
  const triedSrc = useRef(false);

  React.useEffect(() => {
    if (!src) return;
    
    // If we've already tried this URL and it failed, don't try again
    if (error) return;
    
    // If we've already cached this URL successfully, use it
    if (imageCache[src]) {
      setImgSrc(getProxiedUrl(src));
      return;
    }

    if (!triedSrc.current) {
      const img = new window.Image();
      img.onload = () => {
        imageCache[src] = true;
        const proxied = getProxiedUrl(src);
        if (proxied) {
          img.src = proxied;
        }
        setImgSrc(proxied);
        setError(false);
      };
      img.onerror = () => {
        setImgSrc(undefined);
        setError(true);
        // Remove from cache if it fails
        delete imageCache[src];
      };
      const proxied = getProxiedUrl(src);
      if (proxied) {
        img.src = proxied;
      }
      triedSrc.current = true;
    }
  }, [src, error]);

  // Use custom initials logic
  const initials = getInitials(name);

  return (
    <Avatar 
      src={imgSrc} 
      name={initials} 
      size={size} 
      bg={bg} 
      style={style}
      onError={() => setError(true)}
    />
  );
};

const ZelloApiPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [allChannels, setAllChannels] = useState<Channel[]>([]);
  const [channelMap, setChannelMap] = useState<Map<string, Channel>>(new Map());
  const [selectedAddChannels, setSelectedAddChannels] = useState<string[]>([]);
  const [filteredChannels, setFilteredChannels] = useState<Channel[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [isAddingChannels, setIsAddingChannels] = useState(false);
  const [channelSearchQuery, setChannelSearchQuery] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [tabIndex, setTabIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    profile_phone: '',
    tags: ''
  });
  // Pagination state
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const usersPerPage = 50;
  const [currentChannelPage, setCurrentChannelPage] = useState(1);
  const channelsPerPage = 50;
  // Download Audio tab state
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [channelOptions, setChannelOptions] = useState<{label: string, value: string}[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<{label: string, value: string}[]>([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');
  const [prependTimestamps, setPrependTimestamps] = useState(false);
  const [isPrepared, setIsPrepared] = useState(false);
  const [preparedFileUrl, setPreparedFileUrl] = useState<string | null>(null);

  const handleUserSearch = async () => {
    setIsSearching(true);
    try {
      const res = await fetch(`/zello/users?page=1&per_page=50&search=${encodeURIComponent(userSearchQuery)}`);
      const data = await res.json();
      // Filter out admin users
      setUsers((data.users || []).filter((user: User) => !user.admin));
    } catch {
      toast({ title: 'Failed to search users', status: 'error' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleUserClick = async (username: string) => {
    if (!username) {
      toast({ title: 'Invalid username', status: 'error' });
      return;
    }
    setIsLoadingUser(true);
    // Clear QR code when switching users
    setQrCodeUrl(null);
    setIsGeneratingQr(false);
    try {
      const res = await fetch(`/zello/users/get/login/${encodeURIComponent(username)}`);
      const data = await res.json();
      if (data.code === '200' && data.users && data.users.length > 0) {
        const user = data.users[0];
        // Check if user is admin
        if (user.admin) {
          toast({ 
            title: 'Cannot edit admin user', 
            description: 'Admin users cannot be modified through this interface.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          setIsLoadingUser(false);
          return;
        }
        setSelectedUser({
          name: user.name,
          full_name: user.full_name,
          email: user.email,
          profile_phone: user.profile_phone,
          tags: user.tags,
          channels: user.channels || [],
          channels_with_tags: user.channels_with_tags || [],
          admin: user.admin,
          template: user.template,
          profile: user.profile
        });
        setEditForm({
          full_name: user.full_name || '',
          email: user.email || '',
          profile_phone: user.profile_phone || '',
          tags: user.tags || ''
        });

        // Fetch all channels once when modal opens
        fetchAllChannels();

        onOpen();
      } else {
        toast({ title: 'User not found', status: 'error' });
      }
    } catch {
      toast({ title: 'Error loading user info', status: 'error' });
    } finally {
      setIsLoadingUser(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleUserSearch();
    }
  };

  const fetchAllChannels = async () => {
    setIsLoadingChannels(true);
    try {
      const res = await fetch(`/zello/channels`);
      const data = await res.json();
      if (data.code === '200') {
        const sorted = (data.channels || []).sort((a: Channel, b: Channel) => a.name.localeCompare(b.name));
        setAllChannels(sorted);
        setFilteredChannels(sorted);
        // Build channel map for fast lookup
        const map = new Map<string, Channel>();
        for (const ch of sorted) {
          map.set(ch.name, ch);
        }
        setChannelMap(map);
      }
    } catch {
      toast({ title: 'Error loading channels', status: 'error' });
    } finally {
      setIsLoadingChannels(false);
    }
  };

  const handleChannelSearch = (search: string) => {
    setChannelSearchQuery(search);
    if (!search) {
      setFilteredChannels(allChannels);
    } else {
      const filtered = allChannels.filter(ch =>
        ch.name.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredChannels(filtered);
    }
  };

  const handleRemoveFromChannel = async (channel: string) => {
    try {
      const response = await fetch(`/zello/channels/${encodeURIComponent(channel)}/users`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          users: [selectedUser?.name]
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to remove from channel: HTTP ${response.status}`);
      }
      setSelectedUser((prev) => prev ? { ...prev, channels: prev.channels?.filter(c => c !== channel) } : prev);
      toast({ title: `Removed from ${channel}`, status: 'info' });
    } catch {
      toast({ title: 'Error removing from channel', status: 'error' });
    }
  };

  const handleAddToChannels = async () => {
    setIsAddingChannels(true);
    try {
      const responses = await Promise.all(selectedAddChannels.map(ch =>
        fetch(`/zello/channels/${encodeURIComponent(ch)}/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            users: [selectedUser?.name]
          }),
        })
      ));
      const failedResponse = responses.find((response) => !response.ok);
      if (failedResponse) {
        throw new Error(`Failed to add to one or more channels: HTTP ${failedResponse.status}`);
      }
      setSelectedUser((prev) => prev ? { ...prev, channels: [...(prev.channels || []), ...selectedAddChannels] } : prev);
      setSelectedAddChannels([]);
      toast({ title: 'Added to selected channels', status: 'success' });
    } catch {
      toast({ title: 'Error adding to channels', status: 'error' });
    } finally {
      setIsAddingChannels(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedUser) return;
    
    try {
      // Only include fields that have values
      const updateData: any = {};
      if (editForm.full_name) updateData.full_name = editForm.full_name;
      if (editForm.email) updateData.email = editForm.email;
      if (editForm.profile_phone) updateData.profile_phone = editForm.profile_phone;
      if (editForm.tags) updateData.tags = editForm.tags;
      updateData.add = 'false';  // Always include add=false for updates

      const res = await fetch(`/zello/users/${selectedUser.name}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      const data = await res.json();
      if (data.code === '200') {
        // After successful update, reassign to their original template if they had one
        if (selectedUser.template) {
          try {
            const formData = new URLSearchParams();
            formData.append('users[]', selectedUser.name);
            
            const templateResponse = await fetch(`/zello/user/assigntemplate/name/${encodeURIComponent(selectedUser.template)}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: formData.toString(),
            });
            
            const templateData = await templateResponse.json();
            if (templateData.code !== '200') {
              console.error('Error reassigning template:', templateData);
            }
          } catch (error) {
            console.error('Error reassigning template:', error);
          }
        }

        setSelectedUser(prev => prev ? { ...prev, ...updateData } : null);
        setIsEditing(false);
        toast({ title: 'User updated successfully', status: 'success' });
      } else {
        toast({ title: 'Error updating user', status: 'error' });
      }
    } catch {
      toast({ title: 'Error updating user', status: 'error' });
    }
  };

  // Reset pagination when users or channels change
  useEffect(() => {
    setCurrentUserPage(1);
  }, [users]);
  useEffect(() => {
    setCurrentChannelPage(1);
  }, [filteredChannels]);

  // Pagination logic for users
  const paginatedUsers = users.slice((currentUserPage - 1) * usersPerPage, currentUserPage * usersPerPage);
  const totalUserPages = Math.ceil(users.length / usersPerPage);
  // Filter add-to-channels list to exclude already assigned channels
  const assignedChannelNames = new Set(selectedUser?.channels || []);
  const addableChannels = filteredChannels.filter(
    ch => ch && ch.name && !assignedChannelNames.has(ch.name)
  );
  const paginatedChannels = addableChannels.slice((currentChannelPage - 1) * channelsPerPage, currentChannelPage * channelsPerPage);
  const totalChannelPages = Math.ceil(addableChannels.length / channelsPerPage);

  // Fetch channel options for download tab using existing function
  useEffect(() => {
    fetchAllChannels();
  }, []);

  // Update channelOptions when allChannels changes
  useEffect(() => {
    const options = allChannels.map((channel: Channel) => ({ 
      label: channel.name, 
      value: channel.name 
    }));
    setChannelOptions(options);
  }, [allChannels]);

  const handlePrepareDownload = async () => {
    if (!startTime || !endTime || selectedChannels.length === 0) {
      alert('Please select start time, end time, and at least one channel.');
      return;
    }
    
    // Convert datetime-local inputs to epoch timestamps (seconds)
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    
    // Convert local time to UTC epoch timestamps
    const startTs = Math.floor(startDate.getTime() / 1000);
    const endTs = Math.floor(endDate.getTime() / 1000);
    
    console.log('Raw startTime:', startTime);
    console.log('Raw endTime:', endTime);
    console.log('Converted start_ts:', startTs, '(', new Date(startTs * 1000).toISOString(), ')');
    console.log('Converted end_ts:', endTs, '(', new Date(endTs * 1000).toISOString(), ')');
    
    setDownloadLoading(true);
    setDownloadProgress('Preparing audio file (this may take several minutes)...');
    setIsPrepared(false);
    setPreparedFileUrl(null);
    
    try {
      const response = await fetch('/zello/download_audio?prepare=true', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_ts: startTs,
          end_ts: endTs,
          channels: selectedChannels.map(opt => opt.value),
          prepend_timestamps: prependTimestamps
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'processing' && data.file_id) {
        // Start polling for completion
        const pollForCompletion = async () => {
          try {
            const response = await fetch(`/zello/check_prepared/${data.file_id}`);
            if (response.ok) {
              const statusData = await response.json();
              if (statusData.status === 'ready') {
                setDownloadProgress('Audio file ready for download!');
                setIsPrepared(true);
                setPreparedFileUrl(`/zello/download_prepared/${data.file_id}`);
                return;
              } else if (statusData.status === 'error') {
                setDownloadProgress(`Error: ${statusData.error}`);
                setDownloadLoading(false);
                return;
              } else {
                // Still processing, continue polling
                setDownloadProgress('Processing audio files...');
                setTimeout(pollForCompletion, 5000);
              }
            } else {
              throw new Error(`HTTP ${response.status}`);
            }
          } catch (error) {
            console.error('Error checking file status:', error);
            setDownloadProgress('Failed to check file status');
            setDownloadLoading(false);
          }
        };
        
        // Start polling
        setTimeout(pollForCompletion, 2000); // Start polling after 2 seconds
        
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Prepare download error:', error);
      alert(`Failed to prepare audio: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setDownloadProgress('');
      setDownloadLoading(false);
    }
  };

  const handleDownloadFile = () => {
    if (preparedFileUrl) {
      window.open(preparedFileUrl, '_blank');
    }
  };

  const handleResetDownload = () => {
    setIsPrepared(false);
    setPreparedFileUrl(null);
    setDownloadProgress('');
    if (preparedFileUrl) {
      window.URL.revokeObjectURL(preparedFileUrl);
    }
  };

  return (
    <ChakraProvider theme={theme}>
      <Box minH="100vh" bg="gray.50">
        <Box maxW="100%" mx="auto" py={8} px={{ base: 4, md: 8, lg: 12 }}>
          <Heading mb={8} fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}>Zello Admin Panel</Heading>
          <Tabs w="100%" colorScheme="blue" isFitted variant="enclosed" index={tabIndex} onChange={setTabIndex}>
            <TabList mb={6}>
              <Tab fontSize={{ base: "md", md: "lg" }}>Add User</Tab>
              <Tab fontSize={{ base: "md", md: "lg" }}>Edit User</Tab>
              <Tab fontSize={{ base: "md", md: "lg" }}>Download Audio</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box maxW="1200px" mx="auto">
                  <AddUserForm onSuccess={(username) => {
                    setTabIndex(1);
                    handleUserClick(username);
                  }} />
                </Box>
              </TabPanel>
              <TabPanel>
                <Box maxW="100%" mx="auto">
                  <HStack spacing={4} mb={6} w="100%">
                    <InputGroup size="lg" flex="1">
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search users"
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                        isDisabled={isSearching}
                        size="lg"
                      />
                    </InputGroup>
                    <Button
                      colorScheme="blue"
                      onClick={handleUserSearch}
                      isLoading={isSearching}
                      loadingText="Searching..."
                      size="lg"
                      minW="120px"
                    >
                      Search
                    </Button>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                    {paginatedUsers.map((user) => (
                      <Box
                        key={user.name}
                        p={6}
                        borderWidth={1}
                        borderRadius="lg"
                        _hover={{ bg: 'gray.100', cursor: 'pointer', transform: 'translateY(-2px)', transition: 'all 0.2s' }}
                        onClick={() => handleUserClick(user.name)}
                        bg="white"
                        shadow="sm"
                        display="flex"
                        alignItems="center"
                        gap={4}
                      >
                        <CachedAvatar
                          src={user.profile?.picture_thumb || undefined}
                          name={user.full_name || user.name}
                          size="md"
                          style={{ marginRight: '12px' }}
                        />
                        <Box>
                          <Text fontWeight="bold" fontSize="lg">{user.name}</Text>
                          {user.full_name && user.full_name !== user.name && (
                            <Text fontSize="md" color="gray.600">{user.full_name}</Text>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </SimpleGrid>
                  {/* User Pagination Controls */}
                  {totalUserPages > 1 && (
                    <HStack justify="center" mt={6} spacing={4}>
                      <Button
                        onClick={() => setCurrentUserPage((p) => Math.max(1, p - 1))}
                        isDisabled={currentUserPage === 1}
                      >
                        Prev
                      </Button>
                      <Text>
                        Page {currentUserPage} of {totalUserPages}
                      </Text>
                      <Button
                        onClick={() => setCurrentUserPage((p) => Math.min(totalUserPages, p + 1))}
                        isDisabled={currentUserPage === totalUserPages}
                      >
                        Next
                      </Button>
                    </HStack>
                  )}
                </Box>
              </TabPanel>
              <TabPanel>
                <Box maxW="600px" mx="auto" bg="white" p={8} borderRadius="lg" shadow="sm">
                  <Heading size="lg" mb={6}>Download Audio</Heading>
                  <VStack spacing={5} align="stretch">
                    <FormControl isRequired>
                      <FormLabel>Start Time</FormLabel>
                      <Input
                        type="datetime-local"
                        value={startTime}
                        onChange={e => setStartTime(e.target.value)}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>End Time</FormLabel>
                      <Input
                        type="datetime-local"
                        value={endTime}
                        onChange={e => setEndTime(e.target.value)}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Channels</FormLabel>
                      <Select
                        options={channelOptions}
                        isMulti
                        isSearchable
                        placeholder="Select Zello channels..."
                        onChange={(selected: any) => setSelectedChannels(selected || [])}
                        value={selectedChannels}
                        styles={{ menu: (provided: any) => ({ ...provided, zIndex: 9999 }) }}
                      />
                    </FormControl>
                    <FormControl>
                      <Checkbox
                        isChecked={prependTimestamps}
                        onChange={(e) => setPrependTimestamps(e.target.checked)}
                      >
                        Prepend timestamps to audio clips
                      </Checkbox>
                    </FormControl>
                    {!isPrepared ? (
                      <Button
                        colorScheme="blue"
                        onClick={handlePrepareDownload}
                        isLoading={downloadLoading}
                        isDisabled={downloadLoading}
                        size="lg"
                      >
                        {downloadLoading ? 'Preparing Audio File...' : 'Prepare Download'}
                      </Button>
                    ) : (
                      <HStack spacing={4}>
                        <Button
                          colorScheme="green"
                          onClick={handleDownloadFile}
                          size="lg"
                        >
                          Download File
                        </Button>
                        <Button
                          colorScheme="gray"
                          onClick={handleResetDownload}
                          size="lg"
                        >
                          Reset
                        </Button>
                      </HStack>
                    )}
                    {downloadProgress && (
                      <Text fontSize="sm" color={isPrepared ? "green.600" : "blue.600"} textAlign="center">
                        {downloadProgress}
                      </Text>
                    )}
                  </VStack>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        <Modal isOpen={isOpen} onClose={() => {
          onClose();
          // Clear QR code when modal is closed
          setQrCodeUrl(null);
          setIsGeneratingQr(false);
        }} size="6xl">
          <ModalOverlay />
          <ModalContent maxW="1400px" maxH="90vh">
            <ModalHeader fontSize="2xl">User Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto">
              <VStack align="start" spacing={6} w="100%">
                <Box w="100%" bg="white" p={6} borderRadius="lg" shadow="sm">
                  {isEditing ? (
                    <VStack spacing={6} align="stretch" w="100%">
                      <FormControl>
                        <FormLabel fontSize="lg">Full Name</FormLabel>
                        <Input 
                          value={editForm.full_name} 
                          onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                          size="lg"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="lg">Email</FormLabel>
                        <Input 
                          value={editForm.email} 
                          onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                          size="lg"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="lg">Phone</FormLabel>
                        <Input 
                          value={editForm.profile_phone} 
                          onChange={(e) => setEditForm(prev => ({ ...prev, profile_phone: e.target.value }))}
                          size="lg"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="lg">Agency</FormLabel>
                        <ChakraSelect 
                          value={editForm.tags} 
                          onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                          size="lg"
                        >
                          <option value="">Other</option>
                          {tagOptions.filter(opt => opt.value !== '').map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </ChakraSelect>
                      </FormControl>
                      <HStack>
                        <Button colorScheme="blue" onClick={handleEditSubmit} size="lg">Save Changes</Button>
                        <Button onClick={() => setIsEditing(false)} size="lg">Cancel</Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <HStack align="start" justify="space-between" w="100%">
                      <Box flex="1">
                        <Heading size="lg" mb={2}>{selectedUser?.full_name || 'No Full Name'}</Heading>
                        <Text fontSize="lg" color="gray.500" mb={4}>{selectedUser?.name}</Text>
                        {selectedUser?.email && (
                          <Text fontSize="md" color="gray.600" mb={2}>Email: {selectedUser.email}</Text>
                        )}
                        {selectedUser?.profile_phone && (
                          <Text fontSize="md" color="gray.600" mb={2}>Phone: {selectedUser.profile_phone}</Text>
                        )}
                        {selectedUser?.tags && (
                          <Text fontSize="md" color="gray.600" mb={4}>
                            Agency: {tagOptions.find(opt => opt.value === selectedUser.tags)?.label || selectedUser.tags}
                          </Text>
                        )}
                        <HStack spacing={4}>
                          <Button 
                            size="lg" 
                            colorScheme="blue" 
                            onClick={() => setIsEditing(true)}
                          >
                            Edit Details
                          </Button>
                          <Button
                            size="lg"
                            colorScheme="green"
                            onClick={async () => {
                              if (!selectedUser?.email && !selectedUser?.profile_phone) {
                                toast({
                                  title: 'Cannot send invite',
                                  description: 'User must have either an email or phone number to send an invite.',
                                  status: 'error',
                                  duration: 5000,
                                  isClosable: true,
                                });
                                return;
                              }

                              try {
                                const response = await fetch(`/zello/users/${selectedUser.name}/invite`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    email: selectedUser.email || '',
                                    phone: selectedUser.profile_phone || '',
                                    phone_code: '+1' // Default to US phone code
                                  })
                                });

                                const data = await response.json();
                                if (data.code === '200') {
                                  toast({
                                    title: 'Invite sent successfully',
                                    status: 'success',
                                    duration: 3000,
                                    isClosable: true,
                                  });
                                } else {
                                  console.error('Invite error response:', data);
                                  toast({
                                    title: 'Failed to send invite',
                                    description: data.error || 'An error occurred while sending the invite',
                                    status: 'error',
                                    duration: 5000,
                                    isClosable: true,
                                  });
                                }
                              } catch (error) {
                                console.error('Invite error:', error);
                                toast({
                                  title: 'Error sending invite',
                                  description: error instanceof Error ? error.message : 'An unexpected error occurred',
                                  status: 'error',
                                  duration: 5000,
                                  isClosable: true,
                                });
                              }
                            }}
                            isDisabled={!selectedUser?.email && !selectedUser?.profile_phone}
                          >
                            Send Invite
                          </Button>
                          <Button
                            size="lg"
                            colorScheme="blue"
                            onClick={async () => {
                              if (!selectedUser?.email && !selectedUser?.profile_phone) {
                                toast({
                                  title: 'Cannot generate QR code',
                                  description: 'User must have either an email or phone number to generate a QR code.',
                                  status: 'error',
                                  duration: 5000,
                                  isClosable: true,
                                });
                                return;
                              }

                              try {
                                setIsGeneratingQr(true);
                                // Get the ASO code using the same endpoint as invite
                                const response = await fetch(`/zello/users/${selectedUser?.name}/invite`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    email: selectedUser?.email || '',
                                    phone: selectedUser?.profile_phone || '',
                                    phone_code: '+1'
                                  })
                                });

                                const data = await response.json();
                                if (data.code === '200' && data.signOnKey) {
                                  // Add random number to prevent caching
                                  const rnd = Math.random();
                                  setQrCodeUrl(`https://westchesterputnamrtc.zellowork.com/app/qrcode?aso=${data.signOnKey}&rnd=${rnd}`);
                                } else {
                                  toast({
                                    title: 'Failed to generate QR code',
                                    description: 'Could not get sign on key',
                                    status: 'error',
                                    duration: 5000,
                                    isClosable: true,
                                  });
                                  setIsGeneratingQr(false);
                                }
                              } catch (error) {
                                console.error('QR code error:', error);
                                toast({
                                  title: 'Error generating QR code',
                                  description: error instanceof Error ? error.message : 'An unexpected error occurred',
                                  status: 'error',
                                  duration: 5000,
                                  isClosable: true,
                                });
                                setIsGeneratingQr(false);
                              }
                            }}
                            isDisabled={!selectedUser?.email && !selectedUser?.profile_phone}
                          >
                            Show QR Code
                          </Button>
                        </HStack>
                      </Box>
                      <Box ml={8} minW="120px">
                        <CachedAvatar
                          src={selectedUser?.profile?.picture_thumb || undefined}
                          name={selectedUser?.full_name || selectedUser?.name}
                          size="xl"
                          style={{ width: '120px', height: '120px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                        />
                      </Box>
                    </HStack>
                  )}
                </Box>
                {isGeneratingQr && qrCodeUrl && (
                  <Box w="100%" bg="white" p={6} borderRadius="lg" shadow="sm" mt={4}>
                    <VStack spacing={4}>
                      <Heading size="md">QR Code for {selectedUser?.name}</Heading>
                      <Box p={4} bg="gray.50" borderRadius="md">
                        <img 
                          src={qrCodeUrl} 
                          alt="QR Code" 
                          style={{ maxWidth: '300px', height: 'auto' }}
                        />
                      </Box>
                      <Text fontSize="sm" color="gray.600">
                        Scan this QR code with the Zello Work app to sign in
                      </Text>
                      <Button
                        size="md"
                        onClick={() => {
                          setIsGeneratingQr(false);
                          setQrCodeUrl(null);
                        }}
                      >
                        Close QR Code
                      </Button>
                    </VStack>
                  </Box>
                )}
                <Tabs w="100%" colorScheme="blue" isFitted variant="enclosed" size="lg">
                  <TabList>
                    <Tab>Current Channels</Tab>
                    <Tab>Add to Channels</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <Box w="100%" bg="white" p={6} borderRadius="lg" shadow="sm">
                        <VStack align="start" spacing={3} maxH="400px" overflowY="auto" w="100%">
                          {isLoadingChannels ? (
                            <Center p={4}><Spinner size="lg" /></Center>
                          ) : (
                            <>
                              {selectedUser?.channels?.map((channelName) => {
                                const ch = channelMap.get(channelName);
                                return (
                                  <HStack key={channelName} justify="space-between" w="100%" p={4} bg="gray.50" borderRadius="md">
                                    <HStack>
                                      <CachedAvatar
                                        src={getProxiedUrl(ch?.profile?.picture_thumb)}
                                        name={ch?.name || channelName}
                                        size="md"
                                        style={{ marginRight: '12px' }}
                                      />
                                      <Text fontSize="lg">{channelName}</Text>
                                    </HStack>
                                    <Button 
                                      size="md" 
                                      colorScheme="red" 
                                      onClick={() => handleRemoveFromChannel(channelName)}
                                      isDisabled={channelName === 'Everyone'}
                                      opacity={channelName === 'Everyone' ? 0.5 : 1}
                                    >
                                      Remove
                                    </Button>
                                  </HStack>
                                );
                              })}
                              {(!selectedUser?.channels || selectedUser.channels.length === 0) && (
                                <Text color="gray.500" fontSize="lg">No channels assigned</Text>
                              )}
                            </>
                          )}
                        </VStack>
                      </Box>
                    </TabPanel>
                    <TabPanel>
                      <Box w="100%" bg="white" p={6} borderRadius="lg" shadow="sm">
                        <VStack spacing={4} w="100%">
                          <InputGroup size="lg">
                            <InputLeftElement pointerEvents="none">
                              <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                              placeholder="Search channels to add..."
                              value={channelSearchQuery}
                              onChange={(e) => handleChannelSearch(e.target.value)}
                              isDisabled={isLoadingChannels}
                            />
                          </InputGroup>
                          <Box w="100%" maxH="400px" overflowY="auto">
                            {isLoadingChannels ? (
                              <Center p={4}><Spinner size="lg" /></Center>
                            ) : paginatedChannels && paginatedChannels.filter(ch => ch && ch.name).length > 0 ? (
                              paginatedChannels
                                .filter(ch => ch && ch.name)
                                .map((ch) => (
                                  <HStack
                                    key={ch.name}
                                    p={4}
                                    bg="gray.50"
                                    borderRadius="md"
                                    mb={2}
                                    justify="space-between"
                                    _hover={{ bg: 'gray.100' }}
                                    cursor="pointer"
                                    onClick={() => {
                                      if (selectedAddChannels.includes(ch.name)) {
                                        setSelectedAddChannels(prev => prev.filter(c => c !== ch.name));
                                      } else {
                                        setSelectedAddChannels(prev => [...prev, ch.name]);
                                      }
                                    }}
                                  >
                                    <HStack>
                                      <CachedAvatar
                                        src={getProxiedUrl(ch.profile?.picture_thumb)}
                                        name={ch.name}
                                        size="md"
                                        style={{ marginRight: '12px' }}
                                      />
                                      <Box>
                                        <Text fontSize="lg" fontWeight="bold">{ch.name}</Text>
                                        {ch.tags && <Text fontSize="sm" color="gray.500">Tags: {ch.tags}</Text>}
                                      </Box>
                                    </HStack>
                                    <Checkbox
                                      size="lg"
                                      isChecked={selectedAddChannels.includes(ch.name)}
                                      onChange={() => {
                                        if (selectedAddChannels.includes(ch.name)) {
                                          setSelectedAddChannels(prev => prev.filter(c => c !== ch.name));
                                        } else {
                                          setSelectedAddChannels(prev => [...prev, ch.name]);
                                        }
                                      }}
                                    />
                                  </HStack>
                                ))
                            ) : (
                              <Center p={4}>
                                <Text color="gray.500" fontSize="lg">No channels found</Text>
                              </Center>
                            )}
                          </Box>
                          {/* Channel Pagination Controls */}
                          {totalChannelPages > 1 && (
                            <HStack justify="center" mt={6} spacing={4}>
                              <Button
                                onClick={() => setCurrentChannelPage((p) => Math.max(1, p - 1))}
                                isDisabled={currentChannelPage === 1}
                              >
                                Prev
                              </Button>
                              <Text>
                                Page {currentChannelPage} of {totalChannelPages}
                              </Text>
                              <Button
                                onClick={() => setCurrentChannelPage((p) => Math.min(totalChannelPages, p + 1))}
                                isDisabled={currentChannelPage === totalChannelPages}
                              >
                                Next
                              </Button>
                            </HStack>
                          )}
                        </VStack>
                      </Box>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                onClick={handleAddToChannels}
                isDisabled={selectedAddChannels.length === 0 || isAddingChannels}
                isLoading={isAddingChannels}
                loadingText="Adding..."
                size="lg"
              >
                Add Selected Channels
              </Button>
              <Button onClick={onClose} ml={3} size="lg">Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </ChakraProvider>
  );
};

export default ZelloApiPage;
